/**
 * Audit Agent -- Parse Functions Only
 * AI call logic da chuyen sang Workers API (handlers/audit.ts)
 * File nay chi giu lai parse CSV functions cho frontend dung
 */

import { AuditDetails } from '../../types';
import { splitSections, parseSection, parseNumber, parseHumanIndices } from '../shared/csvParser';

// Re-export types for external use
export type { AuditResponse } from '../shared/types';

/**
 * Parse CSV response into AuditDetails object
 */
export const parseCSVAuditResponse = (csvText: string): AuditDetails => {
    const sections = splitSections(csvText);

    // Parse SCORES section
    const scoresContent = sections.get('SCORES') || '';
    const scoresSection = parseSection(scoresContent);
    const scoresRow = scoresSection?.rows[0] || {};

    const overallScore = parseNumber(scoresRow['overall'] || '0');
    const componentScores = {
        accuracy: parseNumber(scoresRow['accuracy'] || '0'),
        terminology: parseNumber(scoresRow['terminology'] || '0'),
        tone: parseNumber(scoresRow['tone'] || '0')
    };

    // Parse SEGMENTS section with enhanced humanIndices parsing
    const segmentsSection = parseSection(sections.get('SEGMENTS') || '');
    const segmentMappings = (segmentsSection?.rows || []).map(row => {
        const humanIndicesResult = parseHumanIndices(row['humanIndices'] || '');
        return {
            sourceIndex: parseNumber(row['sourceIndex'] || '0'),
            humanIndices: humanIndicesResult.indices,
            humanIndicesRaw: humanIndicesResult.rawValue,
            isMissing: humanIndicesResult.isMissing,
            isPartialMatch: humanIndicesResult.isPartialMatch,
            score: parseNumber(row['score'] || '0'),
            rules: row['rules'] || '',
            errors: row['errors'] || '',
            reviews: row['reviews'] || '',
            notes: row['notes'] || ''
        };
    });

    // Parse CRITICAL_ERRORS section
    const errorsSection = parseSection(sections.get('CRITICAL_ERRORS') || '');
    const criticalErrors = (errorsSection?.rows || []).map(row => ({
        original: row['original'] || '',
        ai_choice: row['ai_choice'] || '',
        better_choice: row['better_choice'] || '',
        reason: row['reason'] || '',
        severity: (row['severity'] || 'Warning') as 'Critical Error' | 'Error' | 'Warning'
    }));

    // Parse GLOSSARY_RULES section
    const rulesSection = parseSection(sections.get('GLOSSARY_RULES') || '');
    const glossaryRules = (rulesSection?.rows || []).map(row => ({
        source: row['source'] || '',
        target: row['target'] || '',
        type: (row['type'] || 'terminology') as 'terminology' | 'format' | 'style',
        reason: row['reason'] || ''
    }));

    // Parse ANALYSIS section
    const analysisSection = parseSection(sections.get('ANALYSIS') || '');
    const analysisMap: Record<string, string> = {};
    (analysisSection?.rows || []).forEach(row => {
        analysisMap[row['type'] || ''] = row['content'] || '';
    });

    const isTruncated = !sections.has('ANALYSIS');

    return {
        overallScore,
        componentScores,
        segmentMappings,
        criticalErrors,
        glossaryRules,
        accuracyAnalysis: analysisMap['accuracy'] || '',
        terminologyAnalysis: analysisMap['terminology'] || '',
        toneAnalysis: analysisMap['tone'] || '',
        fluencyAnalysis: analysisMap['fluency'] || '',
        completenessAnalysis: analysisMap['completeness'] || '',
        suggestedPromptUpdate: analysisMap['suggestedPromptUpdate'] || '',
        isTruncated
    };
};
